import React, { ChangeEvent, Fragment, useMemo, useRef, useState } from 'react'
import RightArrowSvg from 'components/RightArrowSvg'
import { StyleUtil } from 'utils/StyleUtil'
import { ToastUtil } from 'utils/ToastUtil'
import { resolveRelativeUrls } from 'utils/HtmlUtil'
import { useCompositeAuth } from 'providers/CompositeAuthProvider'
import closeSvg from 'images/close2.svg'
import copyLinkSvg from 'images/copy-link.svg'
import copyTextSvg from 'images/copy.svg'
import copyEmbedCodeSvg from 'images/copy-embed-code.svg'
import tickSvg from 'images/tick-success.svg'
import warningSvg from 'images/warning.svg'
import { useApi } from 'providers/ApiProvider'
import { Dialog } from '@mui/material'
import { Tooltip } from 'react-tooltip'

interface Props {
  page: string | null
  version?: string
  onClose: () => void
}

type MainStatus =
  | 'upload' // not yet upload
  | 'uploading'
  | 'publish' // also known as uploaded
  | 'publishing'
  | 'published' // finish

type UploadStatus =
  | 'queued'
  | 'uploading'
  | 'uploaded'
  | 'uploadError'
  | 'publishing'
  | 'published'
  | 'publishError'

interface UploadItem {
  id: string
  file: File
  status: UploadStatus
  documentId?: string // generated by backend
  title?: string
  previewUrl?: string
  publishUrl?: string
  error?: string
}

interface ErrorFile {
  name: string
  error: string
}

const MAX_UPLOAD_FILES = 20

const makeId = () => `${crypto.randomUUID()}`

const getUploadMeta = (response: any, fallbackFile: File) => {
  const payload = response?.data ?? response ?? {}

  return {
    documentId: payload.id,
    title: payload.name ?? fallbackFile.name,
    previewUrl: payload.previewUrl ?? '',
  }
}

export default function WidgetBulkUploadTopics({
  page,
  version,
  onClose,
}: Props): JSX.Element {
  const [items, setItems] = useState<UploadItem[]>([])
  const { user } = useCompositeAuth()
  const { upload, publish, getDocumentHtml } = useApi()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [status, setStatus] = useState<MainStatus>('upload')
  const [showWarning, setShowWarning] = useState<boolean>(false)
  const [errorFile, setErrorFile] = useState<ErrorFile | null>(null)

  const uploadedCount = useMemo(
    () => items.filter((item) => item.status === 'uploaded').length,
    [items],
  )

  const uploadErrorCount = useMemo(
    () => items.filter((item) => item.status === 'uploadError').length,
    [items],
  )

  const publishedCount = useMemo(
    () => items.filter((item) => item.status === 'published').length,
    [items],
  )

  const publishErrorCount = useMemo(
    () => items.filter((item) => item.status === 'publishError').length,
    [items],
  )

  const hasUploadedError = useMemo(
    () =>
      items.filter((item) => item.status === 'uploaded').length !==
        items.length && status === 'publish',
    [items, status],
  )

  const hasPublishedError = useMemo(
    () =>
      items.filter((item) => item.status === 'published').length !==
        items.length && status === 'published',
    [items, status],
  )

  const isUploadView = useMemo(() => {
    return status === 'upload' || status === 'uploading'
  }, [status])

  const isPublishView = useMemo(() => {
    return status === 'publish' || status === 'publishing'
  }, [status])

  const isPublishedView = useMemo(() => {
    return status === 'published'
  }, [status])

  const showWarningIcon = useMemo(() => {
    if (isPublishView) return hasUploadedError
    if (isPublishedView) return hasPublishedError
    return false
  }, [hasPublishedError, hasUploadedError, isPublishView, isPublishedView])

  const totalCount = items.length
  const anyUploading = items.some((item) => item.status === 'uploading')
  const anyPublishing = items.some((item) => item.status === 'publishing')

  const getHeaderText = () => {
    switch (status) {
      case 'uploading':
      case 'publish':
        return `${uploadedCount}/${totalCount} uploaded successfully`
      case 'publishing':
      case 'published':
        return `${publishedCount}/${totalCount} published successfully`
      default:
        return 'Drag files here to upload'
    }
  }

  const getErrorText = () => {
    switch (status) {
      case 'uploading':
      case 'publish':
        if (uploadErrorCount > 0) {
          return `${uploadErrorCount}/${totalCount} unable to upload`
        }
        break
      case 'publishing':
      case 'published':
        if (publishErrorCount > 0) {
          return `${publishErrorCount}/${totalCount} unable to publish`
        }
        break
    }
    return ''
  }

  const addFiles = (newFiles: File[]) => {
    if (newFiles.length === 0) return

    // block files if more than MAX
    if (items.length + newFiles.length > MAX_UPLOAD_FILES) {
      ToastUtil.warning(
        `Maximum ${MAX_UPLOAD_FILES} files allowed. Please remove some files and try again.`,
      )
      return
    }

    const nextItems = newFiles.map((file) => ({
      id: makeId(),
      file,
      status: 'queued' as const,
    }))

    setItems((current) => current.concat(nextItems))
  }

  const ALLOWED_EXTENSIONS = ['doc', 'docx', 'docm']

  const isValidFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase()
    return !!ext && ALLOWED_EXTENSIONS.includes(ext)
  }

  const filterValidFiles = (files: File[]) => {
    return files.filter(isValidFile)
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    addFiles(filterValidFiles(files))
    e.target.value = ''
  }

  const handleRemoveFile = (id: string) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }

  const openFilePicker = () => {
    if (!inputRef.current) return
    inputRef.current.value = ''
    inputRef.current.click()
  }

  const hasFiles = (e: React.DragEvent<HTMLDivElement>) => {
    return e.dataTransfer.types.indexOf('Files') > -1
  }

  const copyLink = async (url?: string) => {
    if (!url) {
      ToastUtil.warning('No link available to copy')
      return
    }

    try {
      await navigator.clipboard.writeText(url)
      ToastUtil.success('Link copied')
    } catch (err) {
      console.error(err)
      ToastUtil.error('Failed to copy link')
    }
  }

  const copyEmbedCode = async (url?: string) => {
    if (!url) {
      ToastUtil.warning('No code available to copy')
      return
    }
    const title = 'Course'
    const code = `<iframe width="100%" height="1200" src="${url}" title="${title}" frameborder="0" allow="web-share;fullscreen;"></iframe>`
    try {
      await navigator.clipboard.writeText(code)
      ToastUtil.success('Embed code copied')
    } catch (err) {
      console.error(err)
      ToastUtil.error('Failed to copy embed code')
    }
  }

  const copyHtml = async (id?: string, url?: string) => {
    if (!id || !url) {
      ToastUtil.warning('No code available to copy')
      return
    }
    try {
      const html = await getDocumentHtml(id)
      const absoluteHtml = resolveRelativeUrls(html, url)
      await navigator.clipboard.writeText(absoluteHtml)
      ToastUtil.success('HTML copied')
    } catch (err) {
      console.error(err)
      ToastUtil.error('Failed to copy HTML')
    }
  }

  const copyError = async (text?: string) => {
    if (!text) {
      ToastUtil.warning('No link available to copy')
      return
    }

    try {
      await navigator.clipboard.writeText(text)
      ToastUtil.success('Error message copied')
    } catch (err) {
      console.error(err)
      ToastUtil.error('Failed to copy error')
    }
  }

  const handleUploadAll = async () => {
    setStatus('uploading')
    const pendingItems = items.filter((item) => item.status !== 'uploaded')
    if (pendingItems.length === 0 || anyUploading) {
      ToastUtil.warning('No files can be uploaded')
      return
    }

    // 1. Bulk update UI status to 'uploading' for all pending items at once
    setItems((current) =>
      current.map((currentItem) =>
        pendingItems.some((p) => p.id === currentItem.id)
          ? { ...currentItem, status: 'uploading', error: undefined }
          : currentItem,
      ),
    )

    // 2. Define the single file upload task (returns a Promise)
    const uploadTask = async (item: (typeof pendingItems)[0]) => {
      try {
        const formData = new FormData()
        formData.append('file', item.file)
        formData.append('userId', user?.id || '')
        formData.append('username', user?.username || '')
        formData.append('theme', page || 'mentem')
        if (version) {
          formData.append('version', version)
        }

        const response = await upload(formData)
        const { documentId, title, previewUrl } = getUploadMeta(
          response,
          item.file,
        )

        setItems((current) =>
          current.map((currentItem) =>
            currentItem.id === item.id
              ? {
                  ...currentItem,
                  status: 'uploaded',
                  documentId,
                  title,
                  previewUrl,
                  error: undefined,
                }
              : currentItem,
          ),
        )
        return { success: true }
      } catch (err) {
        console.error(err)
        let errorMessage = 'Unknown error'
        if (typeof err === 'object' && err !== null) {
          errorMessage = (err as any).message ?? errorMessage
        }

        setItems((current) =>
          current.map((currentItem) =>
            currentItem.id === item.id
              ? { ...currentItem, status: 'uploadError', error: errorMessage }
              : currentItem,
          ),
        )
        return { success: false }
      }
    }

    // 3. Execute all upload tasks concurrently using Promise.all
    const results = await Promise.all(
      pendingItems.map((item) => uploadTask(item)),
    )

    // 4. Calculate final success and failure metrics
    const successCount = results.filter((r) => r.success).length
    const failureCount = results.length - successCount

    if (successCount > 0) {
      console.log(`Successfully uploaded ${successCount} file(s)`)
    }
    if (failureCount > 0) {
      console.log(`Failed to upload ${failureCount} file(s)`)
    }

    setStatus('publish')
  }

  const handlePublishAll = async () => {
    setStatus('publishing')
    const pendingItems = items.filter((item) => item.status === 'uploaded')
    if (pendingItems.length === 0 || anyPublishing) {
      ToastUtil.warning('No topics can be published')
      return
    }

    // 1. Bulk update UI status to 'publishing' for all pending items at once
    setItems((current) =>
      current.map((currentItem) =>
        pendingItems.some((p) => p.id === currentItem.id)
          ? { ...currentItem, status: 'publishing', error: undefined }
          : currentItem,
      ),
    )

    // 2. Define the single topic publishing task (returns a Promise)
    const publishTask = async (item: (typeof pendingItems)[0]) => {
      try {
        const documentId = item.documentId
        if (!documentId) throw new Error('Missing documentId')

        const response = await publish(documentId)

        setItems((current) =>
          current.map((currentItem) =>
            currentItem.id === item.id
              ? {
                  ...currentItem,
                  status: 'published',
                  publishUrl: response.publishUrl,
                  error: undefined,
                }
              : currentItem,
          ),
        )
        return { success: true }
      } catch (err) {
        console.error(err)
        let errorMessage = 'Unknown error'
        if (typeof err === 'object' && err !== null) {
          errorMessage = (err as any).message ?? errorMessage
        }

        setItems((current) =>
          current.map((currentItem) =>
            currentItem.id === item.id
              ? { ...currentItem, status: 'publishError', error: errorMessage }
              : currentItem,
          ),
        )
        return { success: false }
      }
    }

    // 3. Execute all publishing tasks concurrently using Promise.all
    const results = await Promise.all(
      pendingItems.map((item) => publishTask(item)),
    )

    // 4. Calculate final success and failure metrics
    const successCount = results.filter((r) => r.success).length
    const failureCount = results.length - successCount

    if (successCount > 0) {
      console.log(`Successfully published ${successCount} file(s)`)
    }
    if (failureCount > 0) {
      console.log(`Failed to publish ${failureCount} file(s)`)
    }

    setStatus('published')
  }

  const handleUploadMore = () => {
    // reset UI
    setItems([])
    setStatus('upload')
  }

  const handleShowWarning = (fileName: string, errorMessage: string) => {
    setErrorFile({
      name: fileName,
      error: errorMessage,
    })
    setShowWarning(true)
  }

  const getProcessingFileText = () => {
    let fileCount = 0
    let processingText = ''
    if (isPublishView) {
      fileCount = uploadedCount
      processingText = 'uploaded'
    } else if (isPublishedView) {
      fileCount = publishedCount
      processingText = 'published'
    } else {
      fileCount = totalCount
      processingText = 'selected'
    }
    const s = fileCount === 1 ? '' : 's'
    return `${fileCount} file${s} ${processingText}`
  }

  return (
    <div className="mt-6 flex flex-row items-center justify-center gap-16">
      <article
        style={{
          width: 560,
          margin: '0 auto',
          paddingTop: 24,
          paddingLeft: 36,
          paddingRight: 36,
          paddingBottom: 36,
          boxShadow:
            '0px 570px 160px 0px rgba(0, 0, 0, 0.00), 0px 365px 146px 0px rgba(0, 0, 0, 0.01), 0px 205px 123px 0px rgba(0, 0, 0, 0.05), 0px 91px 91px 0px rgba(0, 0, 0, 0.09), 0px 23px 50px 0px rgba(0, 0, 0, 0.10)',
        }}
        aria-label="File Upload Modal"
        className="relative flex flex-col bg-white"
        onDrop={(e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault()
          if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files)
            addFiles(filterValidFiles(files))
            e.dataTransfer.clearData()
          }
        }}
        onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
          if (hasFiles(e)) {
            e.stopPropagation()
            e.preventDefault()
          }
        }}
        onDragEnter={(e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault()
          if (!hasFiles(e)) return
        }}
      >
        <div
          style={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 12,
          }}
          onClick={onClose}
        >
          <img src={closeSvg} alt="Close" style={{ height: 24 }} />
        </div>

        <section className="mx-auto flex h-full w-full flex-col overflow-hidden">
          <header className="border-1 flex h-[120px] flex-col items-center justify-center border border-gray-400 gap-[12px]">
            <div className="flex flex-row gap-[12px] items-center justify-center">
              {(isPublishView || isPublishedView) && (
                <img
                  src={showWarningIcon ? warningSvg : tickSvg}
                  alt="SuccessOrWarning"
                  style={{ height: 25 }}
                />
              )}
              <p
                className="flex flex-col justify-start"
                style={{
                  fontWeight: 700,
                  fontSize: 20,
                }}
              >
                <span>{getHeaderText()}</span>
                {getErrorText() !== '' && <span>{getErrorText()}</span>}
              </p>
            </div>

            {isUploadView && (
              <>
                <input
                  data-testid="hidden-input"
                  id="hidden-input"
                  title="hidden-input"
                  accept=".doc,.docx,.docm"
                  type="file"
                  multiple
                  className="hidden"
                  ref={inputRef}
                  onChange={handleFileChange}
                />

                <button
                  data-testid="btn-select"
                  type="button"
                  id="btn-select"
                  onClick={openFilePicker}
                  className={`${
                    page === 'mentem'
                      ? StyleUtil.buttonPurple
                      : StyleUtil.buttonPrimaryUnsw
                  } mb-0 mt-0`}
                  disabled={anyUploading}
                >
                  <span className="pl-2 pr-2">Select files</span>
                </button>
              </>
            )}
          </header>

          {items.length > 0 && (
            <>
              <div className="pt-6">
                {status === 'upload' || status === 'uploading'
                  ? 'Word documents'
                  : 'Page links'}
              </div>

              <div
                style={{
                  marginTop: 6,
                  marginBottom: 24,
                }}
                className="mentem-scrollbar-2 flex max-h-[220px] min-h-[0] flex-col gap-[3px] overflow-y-auto pr-2"
              >
                {items.map((item) => {
                  const isUploading = item.status === 'uploading'
                  const isUploaded = item.status === 'uploaded'
                  const isPublishing = item.status === 'publishing'
                  const isPublished = item.status === 'published'
                  const isError =
                    item.status === 'uploadError' ||
                    item.status === 'publishError'

                  return (
                    <div key={item.id} className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div
                          className="min-w-0 truncate"
                          style={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: 14,
                            fontWeight: 400,
                          }}
                        >
                          {isUploaded && item.previewUrl ? (
                            <Fragment key={`key-preview-url-${item.id}`}>
                              <a
                                href={item.previewUrl}
                                target="__blank"
                                rel="noreferrer"
                                className="cursor-pointer text-secondaryIndigo underline hover:opacity-[0.8]"
                                data-tooltip-id={`tooltip-preview-url-${item.id}`}
                              >
                                {item.title}
                              </a>
                              <Tooltip
                                id={`tooltip-preview-url-${item.id}`}
                                className="mentem-tooltip"
                                style={{
                                  backgroundColor: '#fff',
                                  color: '#000',
                                  borderRadius: '6px',
                                  maxWidth: '500px',
                                  height: 'auto',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                                place="top"
                                noArrow={true}
                              >
                                {item.title}
                              </Tooltip>
                            </Fragment>
                          ) : isPublished && item.publishUrl ? (
                            <Fragment key={`key-publish-url-${item.id}`}>
                              <a
                                href={item.publishUrl}
                                target="__blank"
                                rel="noreferrer"
                                className="cursor-pointer text-secondaryIndigo underline hover:opacity-[0.8]"
                                data-tooltip-id={`tooltip-publish-url-${item.id}`}
                              >
                                {item.title}
                              </a>
                              <Tooltip
                                id={`tooltip-publish-url-${item.id}`}
                                className="mentem-tooltip"
                                style={{
                                  backgroundColor: '#fff',
                                  color: '#000',
                                  borderRadius: '6px',
                                  maxWidth: '500px',
                                  height: 'auto',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                                place="top"
                                noArrow={true}
                              >
                                {item.title}
                              </Tooltip>
                            </Fragment>
                          ) : !isError ? (
                            <span>
                              {isUploaded || isPublishing
                                ? item.title
                                : item.file.name}
                            </span>
                          ) : (
                            <></>
                          )}
                        </div>
                        {isError && (
                          <div
                            style={{
                              fontFamily: 'Clancy',
                              fontSize: 14,
                              fontWeight: 400,
                              color: '#DD2025',
                            }}
                          >
                            {item.file.name || 'Upload failed'}
                          </div>
                        )}
                      </div>

                      <div className="shrink-0">
                        {isUploading || isPublishing ? (
                          <div className="flex h-[14px] w-[14px] items-center justify-center">
                            <span className="inline-block h-[14px] w-[14px] animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                          </div>
                        ) : isUploaded ? (
                          <div className="flex flex-row gap-[4px]">
                            <button
                              data-tooltip-id={`tooltip-copy-link-uploaded-${item.id}`}
                              type="button"
                              className="cursor-pointer flex items-center justify-center"
                              onClick={() => copyLink(item.previewUrl)}
                            >
                              <img
                                src={copyLinkSvg}
                                alt="Copy"
                                style={{ height: 20 }}
                              />
                            </button>
                            <Tooltip
                              id={`tooltip-copy-link-uploaded-${item.id}`}
                              className="mentem-tooltip"
                              style={{
                                backgroundColor: '#fff',
                                color: '#000',
                                borderRadius: '6px',
                              }}
                              place="top"
                              noArrow={true}
                            >
                              Copy link
                            </Tooltip>
                          </div>
                        ) : isPublished ? (
                          <div className="flex flex-row gap-[4px]">
                            <button
                              data-tooltip-id={`tooltip-copy-link-${item.id}`}
                              type="button"
                              className="cursor-pointer flex items-center justify-center"
                              onClick={() => copyLink(item.publishUrl)}
                            >
                              <img
                                src={copyLinkSvg}
                                alt="Copy"
                                style={{ height: 20 }}
                              />
                            </button>
                            <Tooltip
                              id={`tooltip-copy-link-${item.id}`}
                              className="mentem-tooltip"
                              style={{
                                backgroundColor: '#fff',
                                color: '#000',
                                borderRadius: '6px',
                              }}
                              place="top"
                              noArrow={true}
                            >
                              Copy link
                            </Tooltip>
                            <button
                              data-tooltip-id={`tooltip-copy-embed-code-${item.id}`}
                              type="button"
                              className="cursor-pointer flex items-center justify-center"
                              onClick={() => copyEmbedCode(item.publishUrl)}
                            >
                              <img
                                src={copyEmbedCodeSvg}
                                alt="CopyEmbedCode"
                                style={{ height: 20 }}
                              />
                            </button>
                            <Tooltip
                              id={`tooltip-copy-embed-code-${item.id}`}
                              className="mentem-tooltip"
                              style={{
                                backgroundColor: '#fff',
                                color: '#000',
                                borderRadius: '6px',
                              }}
                              place="top"
                              noArrow={true}
                            >
                              Copy embed code
                            </Tooltip>
                            <button
                              data-tooltip-id={`tooltip-copy-html-${item.id}`}
                              type="button"
                              className="cursor-pointer flex items-center justify-center"
                              onClick={() =>
                                copyHtml(item.id, item.publishUrl)
                              }
                            >
                              <img
                                src={copyEmbedCodeSvg}
                                alt="CopyHtml"
                                style={{ height: 20 }}
                              />
                            </button>
                            <Tooltip
                              id={`tooltip-copy-html-${item.id}`}
                              className="mentem-tooltip"
                              style={{
                                backgroundColor: '#fff',
                                color: '#000',
                                borderRadius: '6px',
                              }}
                              place="top"
                              noArrow={true}
                            >
                              Copy HTML
                            </Tooltip>
                          </div>
                        ) : isError ? (
                          <div className="flex flex-row gap-[4px]">
                            <button
                              data-tooltip-id={`tooltip-show-error-${item.id}`}
                              type="button"
                              className="cursor-pointer flex items-center justify-center"
                              onClick={() =>
                                handleShowWarning(
                                  item.file.name,
                                  item.error || '',
                                )
                              }
                            >
                              <img
                                src={warningSvg}
                                alt="Remove"
                                style={{ height: 20 }}
                              />
                            </button>
                            <Tooltip
                              id={`tooltip-show-error-${item.id}`}
                              className="mentem-tooltip"
                              style={{
                                backgroundColor: '#fff',
                                color: '#000',
                                borderRadius: '6px',
                              }}
                              place="top"
                              noArrow={true}
                            >
                              Show error message
                            </Tooltip>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="cursor-pointer flex items-center justify-center"
                            onClick={() => handleRemoveFile(item.id)}
                            disabled={anyUploading}
                          >
                            <img
                              src={closeSvg}
                              alt="Remove"
                              style={{ height: 20 }}
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </section>

        {items.length > 0 && (
          <footer className="flex flex-col gap-[12px]">
            <div
              className="text-right"
              style={{
                fontFamily: 'Roboto',
                fontSize: 12,
              }}
            >
              <span>{getProcessingFileText()}</span>
            </div>

            {isUploadView && (
              <div className="flex justify-center">
                <button
                  data-testid="btn-submit"
                  type="button"
                  id="btn-submit"
                  onClick={handleUploadAll}
                  disabled={
                    anyUploading ||
                    items.every((item) => item.status === 'uploaded')
                  }
                  className={
                    page === 'mentem'
                      ? StyleUtil.buttonPurple + ' !w-64'
                      : StyleUtil.buttonPrimaryUnsw + ' !w-64'
                  }
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span className="pl-2 pr-2">Upload all</span>
                  {page === 'mentem' && (
                    <style>{`button:hover .right-arrow {fill: black;}`}</style>
                  )}
                  <RightArrowSvg
                    className="right-arrow"
                    fill={page === 'mentem' ? 'white' : '#181716'}
                  />
                </button>
              </div>
            )}

            {isPublishView && (
              <div className="flex justify-center">
                <button
                  data-testid="btn-submit"
                  type="button"
                  id="btn-submit"
                  onClick={handlePublishAll}
                  disabled={
                    anyPublishing ||
                    items.every((item) => item.status === 'published')
                  }
                  className={
                    page === 'mentem'
                      ? StyleUtil.buttonSecondary
                      : StyleUtil.buttonSecondaryUnsw
                  }
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span className="pl-2 pr-2">Publish all</span>
                  {page === 'mentem' && (
                    <style>{`button:hover .right-arrow {fill: black;}`}</style>
                  )}
                </button>
              </div>
            )}

            {isPublishedView && (
              <div className="flex justify-center">
                <button
                  data-testid="btn-submit"
                  type="button"
                  id="btn-submit"
                  onClick={handleUploadMore}
                  className={
                    page === 'mentem'
                      ? StyleUtil.buttonPurple + ' !w-64'
                      : StyleUtil.buttonPrimaryUnsw + ' !w-64'
                  }
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span className="pl-2 pr-2">Upload more</span>
                  {page === 'mentem' && (
                    <style>{`button:hover .right-arrow {fill: black;}`}</style>
                  )}
                  <RightArrowSvg
                    className="right-arrow"
                    fill={page === 'mentem' ? 'white' : '#181716'}
                  />
                </button>
              </div>
            )}
          </footer>
        )}
      </article>

      <Dialog
        open={showWarning}
        onClose={() => setShowWarning(false)}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(255, 255, 255, 0.76)',
            },
          },
        }}
        PaperProps={{
          className:
            'relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl',
        }}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={() => setShowWarning(false)}
          className="absolute right-4 top-4 cursor-pointer"
        >
          <img src={closeSvg} alt="Close" className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="text-start">
          <h2 className="mb-[12px] text-[16px] font-[400] font-roboto text-[#DD2025]">
            {errorFile?.name}
          </h2>

          <div className="flex items-center justify-between gap-4">
            <p className="flex-1 text-[16px] font-[400] font-roboto text-[#000]">
              {errorFile?.error}
            </p>

            <button
              data-tooltip-id="tooltip-copy-error"
              type="button"
              className="cursor-pointer flex items-center justify-center shrink-0"
              onClick={() => copyError(errorFile?.error)}
            >
              <img src={copyTextSvg} alt="Copy error" className="h-5 w-5" />
            </button>
            <Tooltip
              id="tooltip-copy-error"
              className="mentem-tooltip"
              style={{
                backgroundColor: '#fff',
                color: '#000',
                borderRadius: '6px',
              }}
              place="top"
              noArrow={true}
            >
              Copy error message
            </Tooltip>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
